import sparkMD5 from 'spark-md5';
// import { getLocaleStore } from '@/i18n';
import { convertImageToWebp, isHttpUri, isMd5Uri, isSupportWebp, loadRawImageAsync } from '@/utils/image-resource-kit';
import { getConfig, uploadBase64ToObs } from '@/utils/upload-kit';

export const loadImageAsBase64 = (imageUrl: string, layerWidth?: number) => {
    // 最小宽度作为可选值，取img.width 和layerWidth
    return new Promise<string>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            // 优化放到svg中的纹理图大小
            canvas.width = layerWidth ? Math.min(img.width, layerWidth) : img.width;
            canvas.height = Math.floor((canvas.width * img.height) / img.width);
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
        };
        img.onerror = (err) => {
            console.error('Error loading image:', err);
            resolve('');
            reject(err);
        };
        img.src = imageUrl;
    });
};

const uploadErrorMap = new Map<string, string>();
// md5 -> base64 的内存映射
const mapping: Map<string, string> = new Map();
// 正在进行中的重新上传 Promise，用于去重
const reuploadingMap = new Map<string, Promise<void>>();
interface IPendingRemoteUrl {
    promise: Promise<string>;
    cancel: () => void;
}
// getRemoteUrlAsync 的去重：同一 code 共享同一个等待任务
const pendingRemoteUrlMap = new Map<string, IPendingRemoteUrl>();
// 同一 code 被强制替换时，阻止旧上传或旧重试回写新映射
const codeGenerationMap = new Map<string, number>();
let generation = 0;

const getCodeGeneration = (code: string) => codeGenerationMap.get(code) ?? 0;

const advanceCodeGeneration = (code: string) => {
    const nextGeneration = getCodeGeneration(code) + 1;
    codeGenerationMap.set(code, nextGeneration);
    return nextGeneration;
};

const isCurrentGeneration = (code: string, taskGeneration: number, taskCodeGeneration: number) =>
    taskGeneration === generation && taskCodeGeneration === getCodeGeneration(code);

/**
 * 上传 Base64 到远程 —— Base64 写入 indexdb 后删除内存映射
 *
 * @param md5 - MD5 编码
 * @param base64 - Base64 编码
 */
const uploadAsync = async (md5: string, base64: string, taskCodeGeneration = getCodeGeneration(md5)) => {
    const taskGeneration = generation;
    try {
        const url = await base64Kit.uploadImgAsync(md5, base64, taskCodeGeneration);
        if (url && isCurrentGeneration(md5, taskGeneration, taskCodeGeneration)) {
            await loadRawImageAsync(url);
            if (isCurrentGeneration(md5, taskGeneration, taskCodeGeneration)) {
                mapping.set(md5, url);
            }
        }
    } catch (e) {
        console.log('upload error => ', e);
    }
};

// 判断是否是 jpg/jpeg 格式
export const isJpgFormat = (url: string): boolean => {
    if (!url) return false;
    // 检查 base64 的 MIME 类型
    if (url.startsWith('data:image/jpeg') || url.startsWith('data:image/jpg')) {
        return true;
    }
    // 检查 URL 路径中的扩展名
    const urlLower = url.toLowerCase();
    const pathname = urlLower.split('?')[0].split('#')[0];
    return /\.(jpg|jpeg)$/i.test(pathname);
};

/**
 * 重新上传图片（去重：同一 md5 同时只会有一个上传任务）
 * @param md5 - MD5 编码
 */
const reuploadAsync = (md5: string, base64: string, attempts: number, timeout = 500) => {
    if (reuploadingMap.has(md5)) {
        return reuploadingMap.get(md5)!;
    }

    const taskGeneration = generation;
    const taskCodeGeneration = getCodeGeneration(md5);
    uploadErrorMap.delete(md5);

    const delay = timeout + timeout * attempts * 0.5;
    const promise = new Promise<void>((resolve) => {
        setTimeout(async () => {
            try {
                if (isCurrentGeneration(md5, taskGeneration, taskCodeGeneration)) {
                    await base64Kit.uploadImgAsync(md5, base64, taskCodeGeneration);
                }
            } finally {
                if (reuploadingMap.get(md5) === promise) {
                    reuploadingMap.delete(md5);
                }
                resolve();
            }
        }, delay);
    });

    reuploadingMap.set(md5, promise);
    return promise;
};

/**
 * Base64 图片管理
 */
export const base64Kit = {
    reset() {
        generation++;
        pendingRemoteUrlMap.forEach(({ cancel }) => cancel());
        uploadErrorMap.clear();
        mapping.clear();
        reuploadingMap.clear();
        pendingRemoteUrlMap.clear();
        codeGenerationMap.clear();
    },

    setRemoteUrl(code: string, url: string) {
        if (isHttpUri(url)) {
            advanceCodeGeneration(code);
            mapping.set(code, url);
        }
    },

    /**
     * 获取远程地址
     *
     * @param code - 代码
     */
    getRemoteUrl(code: string) {
        if (isHttpUri(code)) {
            return code;
        }
        const data = mapping.get(code) || '';
        return isHttpUri(data) ? data : '';
    },
    /**
     * 异步获取远程地址
     *
     * @param code - 代码
     * @param timer - 可选；定时器时间，单位：毫秒；默认值为 `500`
     */
    getRemoteUrlAsync(code: string, timer = 500): Promise<string> {
        if (isHttpUri(code)) {
            return Promise.resolve(code);
        }

        const pending = pendingRemoteUrlMap.get(code);
        if (pending) {
            return pending.promise;
        }

        const taskGeneration = generation;
        const { maxAttempts } = getConfig();
        let start!: () => void;
        let cancel!: () => void;
        const promise = new Promise<string>((resolve, reject) => {
            let attempts = 0;
            let settled = false;
            let timerId: ReturnType<typeof setTimeout> | undefined;

            const cleanup = () => {
                if (timerId !== undefined) {
                    clearTimeout(timerId);
                }
                if (pendingRemoteUrlMap.get(code)?.promise === promise) {
                    pendingRemoteUrlMap.delete(code);
                }
            };

            const resolveOnce = (url: string) => {
                if (settled) return;
                settled = true;
                cleanup();
                resolve(url);
            };

            const rejectOnce = (error: Error) => {
                if (settled) return;
                settled = true;
                cleanup();
                reject(error);
            };

            const getUrl = () => {
                if (taskGeneration !== generation) {
                    rejectOnce(new Error('base64Kit has been reset'));
                    return;
                }
                const url = base64Kit.getRemoteUrl(code);

                if (url) {
                    resolveOnce(url);
                } else {
                    if (mapping.get(code)) {
                        const base64 = uploadErrorMap.get(code);
                        if (base64 && !reuploadingMap.has(code)) {
                            reuploadAsync(code, base64, attempts, 500);
                            attempts++;
                        }

                        if (maxAttempts && attempts >= maxAttempts) {
                            rejectOnce(new Error('upload image failed, please try again'));
                            return;
                        }
                        timerId = setTimeout(getUrl, timer);
                    } else {
                        resolveOnce('');
                    }
                }
            };

            start = getUrl;
            cancel = () => rejectOnce(new Error('base64Kit has been reset'));
        });

        const entry = { promise, cancel };
        pendingRemoteUrlMap.set(code, entry);
        start();
        return promise;
    },
    /**
     * 下载、保存，重新调用上传图片
     *
     * @param code - 代码
     * @param base64 - Base64 编码
     * @param taskCodeGeneration - 可选；调用开始时捕获的 code 代次
     */
    async uploadImgAsync(code: string, base64: string, taskCodeGeneration = getCodeGeneration(code)) {
        if (!code) return code;
        if (isHttpUri(code)) return code;
        if (!isMd5Uri(code)) return code;
        const taskGeneration = generation;
        if (!isCurrentGeneration(code, taskGeneration, taskCodeGeneration)) return;
        const data = mapping.get(code);
        if (!data) return code;
        if (isHttpUri(data)) return data;
        try {
            // console.time(`上传图片 =>${code}`);
            const res = await uploadBase64ToObs(base64);
            if (!isCurrentGeneration(code, taskGeneration, taskCodeGeneration)) return;
            mapping.set(code, res.url);
            // console.timeEnd(`上传图片 =>${code}`);
            // 删除重试记录
            return res.url;
        } catch (e) {
            // 没重试过，需要重新上传
            if (isCurrentGeneration(code, taskGeneration, taskCodeGeneration)) {
                uploadErrorMap.set(code, base64);
            }

            console.log('upload img error => ', e);
        }
    },
    /**
     * 转成 MD5
     *
     * @param item - 元素项
     */
    md5(item: string = ' ') {
        return sparkMD5.hash(item);
    },
    /**
     * 根据文件链接获取 MD5 编码（并同时异步上传文件到 OBS）
     *
     * @param fileUrl - 文件链接
     * @param forceMd5 - 可选；强制使用传入的值作为 MD5 计算
     */
    getCode(fileUrl: string, forceMd5?: string) {
        if (!fileUrl) return fileUrl;
        if (isHttpUri(fileUrl)) {
            return fileUrl;
        }
        if (isMd5Uri(fileUrl)) return fileUrl;
        let md5 = '';
        if (forceMd5) {
            // 使用传入的值作为内容的映射
            md5 = sparkMD5.hash(forceMd5);
        } else {
            md5 = sparkMD5.hash(fileUrl);
        }
        if (mapping.get(md5)) {
            return md5;
        }
        const taskCodeGeneration = advanceCodeGeneration(md5);
        mapping.set(md5, fileUrl);
        uploadAsync(md5, fileUrl, taskCodeGeneration);
        return md5;
    },

    /**
     * 强制替换指定指纹的 Base64 映射并启动最新上传
     *
     * 该操作会推进 code 独立代次，因此相同 code 的旧上传即使晚到也不能覆盖新结果。
     *
     * @param fileUrl - 新的 Base64 图片内容
     * @param fingerprintSource - 用于生成固定 code 的指纹源
     * @returns 新内容对应的 code
     */
    replaceCode(fileUrl: string, fingerprintSource: string) {
        if (!fileUrl) return fileUrl;
        const code = sparkMD5.hash(fingerprintSource);
        const taskCodeGeneration = advanceCodeGeneration(code);
        pendingRemoteUrlMap.get(code)?.cancel();
        uploadErrorMap.delete(code);
        reuploadingMap.delete(code);
        mapping.set(code, fileUrl);
        if (!isHttpUri(fileUrl)) {
            void uploadAsync(code, fileUrl, taskCodeGeneration);
        }
        return code;
    },
    getLocalUrl(code: string) {
        if (isHttpUri(code)) {
            // ATTENTION: 浏览器测的图片展示场景，按统一的策略进行 webp 的云处理参数添加
            if (isSupportWebp() && isJpgFormat(code)) {
                return convertImageToWebp(code);
            }
            return code;
        }
        const url = mapping.get(code) || '';
        // ATTENTION: 浏览器测的图片展示场景，按统一的策略进行 webp 的云处理参数添加
        if (isSupportWebp() && url && isJpgFormat(url)) {
            return convertImageToWebp(url);
        }
        return url || code;
    },

    /**
     * 直接保存为 Base64 编码
     *
     * @param md5 - MD5 编码
     * @param base64 - Base64 编码
     */
    save(md5: string, base64: string): boolean {
        advanceCodeGeneration(md5);
        mapping.set(md5, base64);
        return true;
    },

    /**
     * 是否已存在
     *
     * @param code - 代码
     */
    hasCode(code: string) {
        const data = mapping.get(code);
        return !!data;
    },
};
